import { useState, useEffect, useCallback } from "react";
import { getProjects } from "../services/projectService";

interface ApiError {
  message?: string;
}

interface RawSummary {
  totalProjects?: number;
  totalProjectValue?: { amount?: number };
  actionRequiredCount?: number;
  lenderActionRequiredCount?: number;
  approvedDrawAmount?: { amount?: number };
  lienWaiverCount?: number;
  cscReportCount?: number;
  lenderInfo?: {
    officerName?: string;
    companyName?: string;
    companyAddress?: string;
    branchName?: string;
    branchAddress?: string;
  };
}

interface RawProject {
  project?: RawProjectCore;
  id?: string;
  projectId?: string;
  _id?: string;
  name?: string;
  type?: string;
  status?: string;
  projectStage?: string;
  stage?: string;
  isGcAcknowledge?: boolean;
  gcName?: string;
  lastDrawNumber?: number;
  lastDrawId?: string;
  progress?: number;
  stakeholders?: {
    lenderBranch?: { companyName?: string };
  };
  admin?: { firstName?: string; lastName?: string };
  contractAmount?: number;
  contractValue?: {
    amount?: number;
    currency?: { code?: string; name?: string; symbol?: string };
    contractAmount?: number;
  };
}

interface RawProjectCore extends Omit<RawProject, "project"> {}

export interface LenderInfo {
  officerName: string;
  companyName: string;
  companyAddress: string;
  branchName: string;
  branchAddress: string;
}

export interface DashboardSummary {
  totalProjects: number;
  totalProjectValue: number;
  actionRequiredCount: number;
  lenderActionRequiredCount: number;
  approvedDrawAmount: number;
  lienWaiverCount: number;
  cscReportCount: number;
  lenderInfo: LenderInfo;
}

export interface Project {
  id: string;
  name: string;
  type: string;
  bank: string;
  status: string;
  projectStage: string;
  isGcAcknowledge: boolean;
  admin: { firstName: string; lastName: string };
  contractAmount: number;
  currencySymbol: string;
  gcName: string;
  lastDrawNumber: number | null;
  lastDrawId: string;
  stage: string;
  progress: number;
}

const normalizeSummary = (raw?: RawSummary): DashboardSummary => ({
  totalProjects: raw?.totalProjects ?? 0,
  totalProjectValue: raw?.totalProjectValue?.amount ?? 0,
  actionRequiredCount: raw?.actionRequiredCount ?? 0,
  lenderActionRequiredCount: raw?.lenderActionRequiredCount ?? 0,
  approvedDrawAmount: raw?.approvedDrawAmount?.amount ?? 0,
  lienWaiverCount: raw?.lienWaiverCount ?? 0,
  cscReportCount: raw?.cscReportCount ?? 0,
  lenderInfo: {
    officerName: raw?.lenderInfo?.officerName ?? "",
    companyName: raw?.lenderInfo?.companyName ?? "",
    companyAddress: raw?.lenderInfo?.companyAddress ?? "",
    branchName: raw?.lenderInfo?.branchName ?? "",
    branchAddress: raw?.lenderInfo?.branchAddress ?? "",
  },
});

const normalizeProject = (p: RawProject): Project => {
  const proj: RawProjectCore = p.project ?? p;
  return {
    id: String(proj.id ?? proj.projectId ?? proj._id ?? ""),
    name: proj.name ?? "",
    type: proj.type ?? "",
    bank: proj.stakeholders?.lenderBranch?.companyName ?? "",
    status: proj.status ?? "",
    projectStage: proj.projectStage ?? "",
    isGcAcknowledge: proj.isGcAcknowledge ?? false,
    admin: {
      firstName: proj.admin?.firstName ?? "",
      lastName: proj.admin?.lastName ?? "",
    },
    contractAmount: proj.contractValue?.amount ?? proj.contractAmount ?? 0,
    currencySymbol: proj.contractValue?.currency?.symbol ?? "$",
    gcName: proj.gcName ?? "",
    lastDrawNumber: proj.lastDrawNumber ?? null,
    lastDrawId: proj.lastDrawId ?? "",
    stage: proj.stage ?? proj.projectStage ?? "",
    progress: proj.progress ?? 0,
  };
};

export interface ProjectPagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const useGetProjects = (page = 1, limit = 10) => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [summary, setSummary] = useState<DashboardSummary>(normalizeSummary());
  const [pagination, setPagination] = useState<ProjectPagination>({
    total: 0,
    page: 1,
    limit,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    setError("");

    const { data, error: apiError } = (await getProjects({
      page,
      limit,
    })) as {
      data: {
        data?: RawProject[];
        total?: number;
        page?: number;
        limit?: number;
        totalPages?: number;
        pagination?: ProjectPagination;
        addOns?: { summary?: RawSummary };
      } | null;
      error: ApiError | null;
    };

    if (apiError) {
      setError(apiError.message || "Failed to load projects.");
      setLoading(false);
      return;
    }

    const list = data?.data ?? [];
    setProjects(list.map(normalizeProject));
    setSummary(normalizeSummary(data?.addOns?.summary));
    setPagination(
      data?.pagination ?? {
        total: data?.total ?? list.length,
        page: data?.page ?? page,
        limit: data?.limit ?? limit,
        totalPages:
          data?.totalPages ?? Math.ceil((data?.total ?? list.length) / limit),
      },
    );
    setLoading(false);
  }, [page, limit]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    summary,
    pagination,
    loading,
    error,
    refetch: fetchProjects,
  };
};

export default useGetProjects;
